import { ILibraryFolder } from "@/api";
import { LibraryTab } from "@/api/library-documents/library-documents.types";
import SearchBar from "@/components/shared/SearchBar";
import {
    IBreadcrumbSegment,
    ILibraryDragItem,
} from "@/components/shared/document-picker/DocumentPicker.types";
import { PickerBreadcrumb } from "@/components/shared/document-picker/PickerBreadcrumb";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useScopesEnabled } from "@/contexts/frontend/organization/useScopesEnabled";
import { useQueryClient } from "@tanstack/react-query";
import {
    BookOpen,
    FolderPlus,
    LayoutTemplate,
    Plus,
    Upload,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { isValidDropForTarget } from "../utils/dropValidation";
import {
    getAncestorChain,
    isForeignSharedSubject,
} from "../utils/folderTreeUtils";
import {
    getTypeFilterOptions,
    isValidTypeFilter,
    LibraryTypeFilter,
} from "../utils/libraryItemTypes";
import { LIBRARY_TAB_ROUTES } from "../utils/libraryTabRoutes";
import { executeMoveWithUndo } from "../utils/moveWithUndo";
import { sharedKnowledgeBaseLabel } from "../utils/sharedKnowledgeBaseCopy";
import { TemplatesHelpPopover } from "./templates/TemplatesHelpPopover";

interface IFilterBarProps {
    activeTab: LibraryTab;
    searchValue: string;
    onSearchChange: (value: string) => void;
    onSearchClear: () => void;
    typeFilter: LibraryTypeFilter;
    onTypeFilterChange?: (value: LibraryTypeFilter) => void;
    onUploadClick?: () => void;
    onCreateFolderClick?: () => void;
    currentFolder: ILibraryFolder | null;
    folders: ILibraryFolder[];
    /** Why New is unavailable, as the host page decides it; null leaves it
     *  available. */
    newUnavailableReason?: string | null;
    onBreadcrumbClick?: () => void;
    onFolderNavigate?: (folderId: number | null) => void;
    onTabChange?: (tab: LibraryTab) => void;
    hideActions?: boolean;
    /** Master gate: hides BOTH the tab switcher and the type filter. */
    hideFilters?: boolean;
    /** Hides ONLY the Documents/Knowledge-Base tab switcher, leaving the
     * type filter independently visible (Client Hub's scoped Library tab).
     * Default `false`, so the global library renders unchanged. Redundant
     * (but harmless) when `hideFilters` is set. */
    hideTabs?: boolean;
    /** Hides ONLY the type filter, leaving the tab switcher visible. */
    hideTypeFilter?: boolean;
    /** Opt-in third tab for the org Templates library. Only the main Document
     * Library surface shows it; folder-move / client-scoped FilterBars leave it
     * off so templates (an org-level, non-folder surface) is never offered as a
     * move target or inside a client workspace. */
    showTemplatesTab?: boolean;
    /** Hides ONLY the "Document Library" h1, leaving the New menu (gated by
     * `hideActions`) available: heading-off + actions-on for the Client
     * Hub's scoped Library tab. Default `false`; redundant (but harmless)
     * when `hideActions` is set, which already removes the heading. */
    hideTitle?: boolean;
    /** Caller-supplied breadcrumb segments — the Unit 7 seam
     * (insurescope-7149k.3.1.5). When present, the internal path-based
     * builder below is BYPASSED and these render verbatim through
     * PickerBreadcrumb. The client workspace builds `?folder=`-based
     * segments (root = client name, zero router navigation); the main
     * library never passes this, so its builder stays byte-for-byte
     * (FilterBar.test.tsx [18]-[23] are the canary). Deliberately NOT a
     * third LibraryTab branch: callers own their navigation model. */
    breadcrumbSegments?: IBreadcrumbSegment[];
    onDropComplete?: () => void;
    /** The client workspace breadcrumb drops act in, when any: threaded
     * into executeMoveWithUndo so the move AND its undo invalidate
     * ["client", id] (the workspace header). The undo leg never fires
     * onDropComplete, so without this the header goes stale after an
     * undo (insurescope-g5bqp). The main library never passes it. */
    moveClientId?: number;
}

export const FilterBar = ({
    activeTab,
    searchValue,
    onSearchChange,
    onSearchClear,
    typeFilter,
    onTypeFilterChange,
    onUploadClick,
    onCreateFolderClick,
    currentFolder,
    folders,
    newUnavailableReason = null,
    onBreadcrumbClick,
    onFolderNavigate,
    onTabChange,
    hideActions = false,
    hideFilters = false,
    hideTabs = false,
    hideTypeFilter = false,
    showTemplatesTab = false,
    hideTitle = false,
    breadcrumbSegments,
    onDropComplete,
    moveClientId,
}: IFilterBarProps) => {
    const scopesEnabled = useScopesEnabled();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();

    const handleBreadcrumbDrop = useCallback(
        (
            folderId: number | null,
            folderName: string,
            items: ILibraryDragItem[]
        ) => {
            executeMoveWithUndo(
                items,
                folderId,
                folderName,
                queryClient,
                onDropComplete,
                { clientId: moveClientId }
            );
        },
        [queryClient, onDropComplete, moveClientId]
    );

    const checkBreadcrumbDrop = useCallback(
        (folderId: number | null, items: ILibraryDragItem[]) =>
            isValidDropForTarget(folders, items, folderId),
        [folders]
    );

    const handleTabChange = (value: string) => {
        const tab = value as LibraryTab;
        if (onTabChange) {
            onTabChange(tab);
        } else {
            navigate(LIBRARY_TAB_ROUTES[tab]);
        }
    };

    const isForeignFolder =
        currentFolder !== null && isForeignSharedSubject(currentFolder);

    const isDocumentsTab = activeTab === "documents";
    const isTemplatesTab = activeTab === "templates";
    const rootLabel = isDocumentsTab
        ? "Documents"
        : isTemplatesTab
          ? "Templates"
          : "Knowledge Base";
    const rootPath = isDocumentsTab
        ? "/organizations/document-library/documents"
        : isTemplatesTab
          ? "/organizations/document-library/templates"
          : "/organizations/document-library/knowledge-base";

    const kbRootFolder = useMemo(
        () => folders.find((f) => f.is_knowledge_base && !f.parent_id) ?? null,
        [folders]
    );

    const breadcrumbs: IBreadcrumbSegment[] = useMemo(() => {
        const search = searchParams.toString();
        const withSearch = (path: string) =>
            search ? `${path}?${search}` : path;

        const rootFolderId = isDocumentsTab ? null : (kbRootFolder?.id ?? null);

        const handleBreadcrumbNav = (path: string, folderId: number | null) => {
            if (onFolderNavigate) {
                onFolderNavigate(folderId);
            } else {
                navigate(withSearch(path));
            }
            onBreadcrumbClick?.();
        };

        if (!currentFolder) {
            return [
                {
                    label: rootLabel,
                    onClick: () => {},
                    isCurrent: true,
                    folderId: rootFolderId,
                },
            ];
        }

        const ancestors = getAncestorChain(folders, currentFolder);

        // On KB tab, skip the KB root folder (tab toggle already represents it).
        if (
            ancestors.length > 0 &&
            ancestors[0].is_knowledge_base &&
            !ancestors[0].parent_id
        ) {
            ancestors.shift();
        }

        const buildFolderPath = (folder: ILibraryFolder) =>
            folder.is_knowledge_base
                ? `/organizations/document-library/knowledge-base/folder/${folder.id}`
                : `/organizations/document-library/documents/folder/${folder.id}`;

        return [
            {
                label: rootLabel,
                onClick: () => handleBreadcrumbNav(rootPath, rootFolderId),
                isCurrent: false,
                folderId: rootFolderId,
            },
            ...ancestors.slice(0, -1).map((folder) => ({
                label: folder.name,
                onClick: () =>
                    handleBreadcrumbNav(buildFolderPath(folder), folder.id),
                isCurrent: false,
                folderId: folder.id,
            })),
            {
                label: currentFolder.name,
                onClick: () => {},
                isCurrent: true,
                folderId: currentFolder.id,
            },
        ];
    }, [
        currentFolder,
        rootLabel,
        rootPath,
        isDocumentsTab,
        kbRootFolder,
        navigate,
        folders,
        searchParams,
        onBreadcrumbClick,
        onFolderNavigate,
    ]);

    const typeItems = getTypeFilterOptions(scopesEnabled).map((opt) => ({
        value: opt.value,
        label: (
            <span className="flex items-center gap-2">
                {"icon" in opt && opt.icon}
                {opt.label}
            </span>
        ),
    }));

    return (
        <div className="@container flex flex-col gap-2 pb-2">
            {!hideActions && !hideTitle && (
                <h1 className="text-primary mt-1 mb-0.5 text-2xl font-bold tracking-tight break-words @max-sm:text-xl">
                    Document Library
                </h1>
            )}

            <div className="flex flex-wrap items-center gap-2 @max-xl:flex-col @max-xl:items-stretch">
                {!hideFilters && !hideTabs && (
                    <Tabs
                        value={activeTab}
                        onValueChange={handleTabChange}
                        className="@max-xl:w-full"
                    >
                        <TabsList className="h-9 @max-xl:w-full">
                            <TabsTrigger value="documents">
                                Documents
                            </TabsTrigger>
                            <TabsTrigger value="knowledge_base">
                                Knowledge Base
                            </TabsTrigger>
                            {showTemplatesTab && (
                                <TabsTrigger value="templates">
                                    Templates
                                </TabsTrigger>
                            )}
                        </TabsList>
                    </Tabs>
                )}

                {!(hideFilters || hideTypeFilter) && isDocumentsTab && (
                    <Select
                        items={typeItems}
                        value={typeFilter}
                        onValueChange={(v) => {
                            if (v && isValidTypeFilter(v)) {
                                onTypeFilterChange?.(v);
                            }
                        }}
                    >
                        <SelectTrigger
                            aria-label="Filter by type"
                            className="min-w-[155px] @max-xl:w-full"
                        >
                            <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                            {typeItems.map(({ value, label }) => (
                                <SelectItem key={value} value={value}>
                                    {label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}

                <SearchBar
                    value={searchValue}
                    onChange={onSearchChange}
                    onClear={onSearchClear}
                    placeholder="Search..."
                />

                {!hideActions &&
                    (isTemplatesTab ? (
                        <Button
                            className="@max-xl:w-full"
                            onClick={() => onUploadClick?.()}
                        >
                            <Upload className="size-4" />
                            Upload template
                        </Button>
                    ) : newUnavailableReason ? (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger
                                    render={
                                        <Button
                                            aria-disabled
                                            className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50 @max-xl:w-full"
                                        />
                                    }
                                >
                                    <Plus className="size-4" />
                                    New
                                </TooltipTrigger>
                                <TooltipContent>
                                    {newUnavailableReason}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ) : (
                        <DropdownMenu>
                            <DropdownMenuTrigger
                                render={
                                    <Button className="@max-xl:w-full">
                                        <Plus className="size-4" />
                                        New
                                    </Button>
                                }
                            />
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    onClick={() => onCreateFolderClick?.()}
                                >
                                    <FolderPlus className="size-4" />
                                    New Folder
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => onUploadClick?.()}
                                >
                                    <Upload className="size-4" />
                                    Upload Document
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ))}
            </div>

            {!hideActions &&
                activeTab === "knowledge_base" &&
                !searchValue &&
                !newUnavailableReason && (
                    <div className="bg-primary/5 border-primary/15 mt-0.5 flex items-center gap-2 rounded-md border px-3 py-2">
                        <BookOpen size={14} className="text-primary shrink-0" />
                        <p className="text-muted-foreground text-xs">
                            Add documents here for InsureScope&apos;s AI to
                            reference. Upload best practices, approved language,
                            SOPs, and other guidelines your team follows.
                        </p>
                    </div>
                )}

            {!hideActions && isTemplatesTab && !searchValue && (
                <div className="bg-primary/5 border-primary/15 mt-0.5 flex items-center gap-2 rounded-md border px-3 py-2">
                    <LayoutTemplate
                        size={14}
                        className="text-primary shrink-0"
                    />
                    <p className="text-muted-foreground text-xs">
                        Upload a fillable spreadsheet or PDF and Polly fills a
                        copy from your workflow conversations. Each new template
                        compiles in the background, then is ready to reuse.
                    </p>
                    <div className="flex-1" />

                    <TemplatesHelpPopover />
                </div>
            )}

            <PickerBreadcrumb
                segments={breadcrumbSegments ?? breadcrumbs}
                maxVisible={4}
                onDrop={hideActions ? undefined : handleBreadcrumbDrop}
                isValidDrop={hideActions ? undefined : checkBreadcrumbDrop}
            />

            {isForeignFolder ? (
                <p className="text-muted-foreground text-xs">
                    {`Documents here belong to ${sharedKnowledgeBaseLabel(currentFolder.shared_group_name)}.`}{" "}
                    You can browse, open and download them.
                </p>
            ) : (
                currentFolder?.is_shared && (
                    <p className="text-muted-foreground text-xs">
                        Documents here are available to Polly and skills in your
                        group&apos;s member organizations. Their users can
                        browse, open and download them.
                    </p>
                )
            )}
        </div>
    );
};
